import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {CnImportApiService} from '../../../service/api/cn-import-api/cn-import-api.service';
import {CnImportDraftDto} from '../../../model/dto/api/cn-import-draft.dto';
import {CnImportApproveResultDto} from '../../../model/dto/api/cn-import-approve-result.dto';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ProtectedComponent
 * Reason: Eingangsfreigabe (P3) — global DRAFT review/approval for commission imports. Loads ALL
 *         open import drafts across every Gesellschaft (no partner pre-selection) and lets the user
 *         select-all + approve/reject in bulk. Clicking a row opens the cn.CommissionStatement
 *         detail OBC in a right-side master-detail panel (Fiori-style), mirroring the
 *         BmPaymentReconciliationAccountsComponent grid/master-detail layout.
 */

/**
 * Provisions-Eingangsfreigabe component (P3), hosted by CnImportReviewPage.
 *
 * Lets a Sachbearbeiter:
 *  1. review ALL open DRAFT import statements across every Gesellschaft
 *     (CnImportApiService.listImportDrafts() — called with NO commissionPartnerId),
 *  2. select-all + approve in bulk (CnImportApiService.approveDrafts(ids)) — on success a German
 *     confirmation summarises how many Belege were released and whether the started Abgleich left
 *     them RECONCILED or in CLARIFICATION,
 *  3. or reject the selection in bulk with an optional reason
 *     (CnImportApiService.rejectDrafts(ids, reason)),
 *  4. open one draft's CommissionStatement detail (Übersicht / Buchungen / Zeilen / Dokument) in a
 *     right-side master-detail panel by clicking the Kontoauszug-Nr; the embedded
 *     mvs-cn-commission-statement-object-component loads itself from the supplied ObjectIdentifier.
 *
 * NO business logic runs in the FE — the drafts, the approve outcome (activities created, periods
 * reconciled, run ids) and the reject result all come from the backend. The component owns the
 * global list, the bulk selection, the right-side detail selection and the idle / busy / ready /
 * error UI states only.
 */
@Component({
    selector: 'mvs-cn-import-review',
    templateUrl: './cn-import-review.component.html',
    styleUrls: ['./cn-import-review.component.scss'],
    standalone: false
})
export class CnImportReviewComponent implements OnInit, OnChanges, OnDestroy {

    /** Statement status NAME the backend reports for a reconciled approval outcome. */
    static readonly STATUS_RECONCILED = 'RECONCILED';
    /** Statement status NAME the backend reports when an approval needs clarification. */
    static readonly STATUS_CLARIFICATION = 'CLARIFICATION';

    // ===== Drafts list =====
    /** All open DRAFT statements across every Gesellschaft. */
    drafts: CnImportDraftDto[] = [];
    /** List UI state: busy / ready / error. */
    listState: 'busy' | 'ready' | 'error' = 'busy';
    /** Friendly German list-load error, or null. */
    listError: string | null = null;

    // ===== Bulk selection =====
    /** The currently selected drafts (two-way bound to the p-table [(selection)]). */
    selectedDrafts: CnImportDraftDto[] = [];

    // ===== Right-side master-detail =====
    /** The statement id whose detail OBC is open in the right-side panel, or null when closed. */
    selectedStatementId: number | null = null;
    /** ObjectIdentifier handed to the embedded cn.CommissionStatement OBC (it self-loads from it). */
    selectedStatementObjectIdentifier: ObjectIdentifier | null = null;

    // ===== Approve / reject actions =====
    /** True while an approve OR reject request is in flight (disables the action buttons). */
    actionBusy: boolean = false;
    /** Friendly German action error (approve/reject), or null. */
    actionError: string | null = null;
    /** German confirmation summary of the last successful approve, or null. */
    approveResultMessage: string | null = null;
    /** German confirmation summary of the last successful reject, or null. */
    rejectResultMessage: string | null = null;

    // ===== Reject reason dialog =====
    /** Whether the reject-reason dialog is open. */
    rejectDialogVisible: boolean = false;
    /** The optional, free-text rejection reason entered in the dialog. */
    rejectReason: string = '';

    initialized: boolean = false;

    constructor(
        private importApiService: CnImportApiService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * React to bound input changes. The component is currently self-contained (no @Input bindings);
     * the hook is implemented to honour the module lifecycle convention and to keep the contract for
     * any future page-supplied context inputs.
     */
    ngOnChanges(): void {
    }

    /**
     * Clean up on destroy. The component holds no timers or long-lived subscriptions (every HTTP
     * call self-completes), so there is nothing to tear down; the hook honours the module convention.
     */
    ngOnDestroy(): void {
    }

    /**
     * Initialize the component: immediately load the global open-drafts list (no Gesellschaft
     * pre-selection is required any more).
     */
    initComponent(): void {
        this.loadDrafts();
        this.initialized = true;
    }

    /**
     * Load ALL open DRAFT import statements across every Gesellschaft
     * (GET /cn/import/drafts — no commissionPartnerId). Drives the busy / ready / error list states.
     */
    loadDrafts(): void {
        this.listState = 'busy';
        this.listError = null;
        this.importApiService.listImportDrafts().subscribe({
            next: (rows) => {
                this.drafts = rows ?? [];
                this.selectedDrafts = [];
                this.listState = 'ready';
            },
            error: (err) => {
                this.drafts = [];
                this.selectedDrafts = [];
                this.listState = 'error';
                this.listError = cnErrorMessage(err);
            }
        });
    }

    /** Whether at least one draft is currently selected. */
    get hasSelection(): boolean {
        return this.selectedDrafts.length > 0;
    }

    /** Whether the right-side detail panel is currently open. */
    get detailOpen(): boolean {
        return this.selectedStatementId != null;
    }

    /** The statement ids of the current selection (the bulk approve/reject payload). */
    private get selectedStatementIds(): number[] {
        return this.selectedDrafts.map(d => d.statementId);
    }

    // =====================================================================================
    // Right-side master-detail
    // =====================================================================================

    /**
     * Open the CommissionStatement detail OBC for a draft row in the right-side panel. Builds the
     * ObjectIdentifier {cn.CommissionStatement, statementId} the embedded OBC self-loads from. Used
     * by the clickable Kontoauszug-Nr cell; the bulk select/approve/reject flow is unaffected (the
     * checkbox column owns the selection, this only opens the detail).
     */
    openStatement(statementId: number): void {
        if (statementId == null) {
            return;
        }
        this.selectedStatementId = statementId;
        this.selectedStatementObjectIdentifier = new ObjectIdentifier('cn.CommissionStatement', statementId);
    }

    /** Close the right-side detail panel (the drafts table reclaims the full width). */
    closeDetail(): void {
        this.selectedStatementId = null;
        this.selectedStatementObjectIdentifier = null;
    }

    // =====================================================================================
    // Approve (Freigeben)
    // =====================================================================================

    /**
     * Approve the selected drafts in one request (POST /cn/import/approve-bulk). On success the
     * backend creates the commission activities and triggers the Abgleich for each statement; a
     * German confirmation summarises the result (with the per-statement RECONCILED / CLARIFICATION
     * breakdown), the list reloads and the selection clears.
     */
    approveSelected(): void {
        if (this.actionBusy || !this.hasSelection) {
            return;
        }
        const ids = this.selectedStatementIds;
        this.startAction();
        this.importApiService.approveDrafts(ids).subscribe({
            next: (results) => {
                this.approveResultMessage = this.buildApproveSummary(results ?? []);
                this.finishAction();
            },
            error: (err) => this.onActionError(err)
        });
    }

    /**
     * Build the German approve confirmation from the per-statement results: how many Belege were
     * released, that the Abgleich has started, and — when the backend reports it — how many landed
     * RECONCILED vs. in CLARIFICATION.
     */
    private buildApproveSummary(results: CnImportApproveResultDto[]): string {
        const count = results.length;
        const reconciled = results.filter(r => r.status === CnImportReviewComponent.STATUS_RECONCILED).length;
        const clarification = results.filter(r => r.status === CnImportReviewComponent.STATUS_CLARIFICATION).length;

        let summary = `${count} ${count === 1 ? 'Beleg' : 'Belege'} freigegeben. Abgleich gestartet.`;
        const parts: string[] = [];
        if (reconciled > 0) {
            parts.push(`${reconciled} abgeglichen`);
        }
        if (clarification > 0) {
            parts.push(`${clarification} in Klärung`);
        }
        if (parts.length > 0) {
            summary += ` (${parts.join(', ')})`;
        }
        return summary;
    }

    // =====================================================================================
    // Reject (Ablehnen)
    // =====================================================================================

    /**
     * Open the reject-reason dialog for the current selection. The reason is optional, so the dialog
     * is just a small prompt the user can confirm straight away.
     */
    openRejectDialog(): void {
        if (this.actionBusy || !this.hasSelection) {
            return;
        }
        this.rejectReason = '';
        this.actionError = null;
        this.rejectDialogVisible = true;
    }

    /** Close the reject-reason dialog WITHOUT any server call. */
    cancelReject(): void {
        if (this.actionBusy) {
            return;
        }
        this.rejectDialogVisible = false;
        this.rejectReason = '';
    }

    /**
     * Reject the selected drafts in one request (POST /cn/import/reject-bulk) with the optional
     * reason. On success a German confirmation summarises how many Belege were rejected, the list
     * reloads and the selection clears.
     */
    confirmReject(): void {
        if (this.actionBusy || !this.hasSelection) {
            return;
        }
        const ids = this.selectedStatementIds;
        const reason = this.rejectReason.trim();
        this.startAction();
        this.importApiService.rejectDrafts(ids, reason).subscribe({
            next: (rejectedIds) => {
                const count = (rejectedIds ?? []).length;
                this.rejectResultMessage =
                    `${count} ${count === 1 ? 'Beleg' : 'Belege'} abgelehnt.`;
                this.rejectDialogVisible = false;
                this.rejectReason = '';
                this.finishAction();
            },
            error: (err) => this.onActionError(err)
        });
    }

    // =====================================================================================
    // Shared action lifecycle
    // =====================================================================================

    /** Mark an approve/reject action as started: busy + clear stale errors / result feedback. */
    private startAction(): void {
        this.actionBusy = true;
        this.actionError = null;
        this.approveResultMessage = null;
        this.rejectResultMessage = null;
    }

    /**
     * Shared post-action success teardown: clear busy, drop the selection, close any open detail
     * panel (the released / rejected statement leaves the list) and reload the drafts.
     */
    private finishAction(): void {
        this.actionBusy = false;
        this.selectedDrafts = [];
        this.closeDetail();
        this.loadDrafts();
    }

    /**
     * Action failure: surface a friendly German message (never a raw HttpErrorResponse), keep the
     * selection so the user can retry, and leave any open dialog open.
     */
    private onActionError(err: unknown): void {
        this.actionBusy = false;
        this.actionError = cnErrorMessage(err);
    }
}
